import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router';
import { useAuthStore } from '../stores/auth';

const routes: RouteRecordRaw[] = [
  { path: '/', name: 'home', component: () => import('../views/HomePage.vue') },
  { path: '/login', name: 'login', component: () => import('../views/LoginPage.vue'), meta: { chromeless: true } },
  { path: '/register', name: 'register', component: () => import('../views/RegisterPage.vue'), meta: { chromeless: true } },
  { path: '/verify-email', name: 'verify-email', component: () => import('../views/VerifyEmailPage.vue'), meta: { chromeless: true } },
  { path: '/reset-password', name: 'reset-password', component: () => import('../views/ResetPasswordPage.vue'), meta: { chromeless: true } },
  { path: '/catalog', name: 'catalog', component: () => import('../views/CatalogPage.vue') },
  { path: '/catalog/:id', name: 'product', component: () => import('../views/ProductPage.vue'), props: true },
  { path: '/orders', name: 'orders', component: () => import('../views/OrdersPage.vue'), meta: { requiresAuth: true } },
  { path: '/orders/:id', name: 'order-detail', component: () => import('../views/OrderDetailPage.vue'), props: true, meta: { requiresAuth: true } },
  { path: '/cart', name: 'cart', component: () => import('../views/CartPage.vue'), meta: { requiresAuth: true } },
  { path: '/favorites', name: 'favorites', component: () => import('../views/FavoritesPage.vue'), meta: { requiresAuth: true } },
  { path: '/checkout', name: 'checkout', component: () => import('../views/CheckoutPage.vue'), meta: { requiresAuth: true, chromeless: true } },
  { path: '/wallet', redirect: '/account/billing' },
  { path: '/wallet/topup/return', name: 'topup-return', component: () => import('../views/TopupReturnPage.vue'), meta: { requiresAuth: true } },
  { path: '/account', name: 'account', component: () => import('../views/DashboardPage.vue'), meta: { requiresAuth: true } },
  { path: '/account/settings', name: 'account-settings', component: () => import('../views/SettingsPage.vue'), meta: { requiresAuth: true } },
  { path: '/account/billing', name: 'account-billing', component: () => import('../views/BillingPage.vue'), meta: { requiresAuth: true } },
  { path: '/profile', redirect: '/account' },
  { path: '/articles', name: 'articles', component: () => import('../views/ArticlesPage.vue') },
  { path: '/articles/:slug', name: 'article', component: () => import('../views/ArticlePage.vue'), props: true, meta: { deferPageView: true } },
  { path: '/help', name: 'help', component: () => import('../views/HelpPage.vue') },
  { path: '/legal/terms', name: 'legal-terms', component: () => import('../views/legal/TermsPage.vue') },
  { path: '/legal/privacy', name: 'legal-privacy', component: () => import('../views/legal/PrivacyPage.vue') },
  { path: '/legal/cookies', name: 'legal-cookies', component: () => import('../views/legal/CookiesPage.vue') },
  { path: '/legal/:slug', name: 'legal-cms', component: () => import('../views/legal/CmsLegalPage.vue'), props: true },
  {
    path: '/admin',
    component: () => import('../views/admin/AdminLayout.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
    children: [
      { path: '', name: 'admin-dashboard', component: () => import('../views/admin/AdminDashboard.vue') },
      { path: 'orders', name: 'admin-orders', component: () => import('../views/admin/AdminOrders.vue') },
      { path: 'orders/:id', name: 'admin-order-detail', component: () => import('../views/admin/AdminOrderDetail.vue') },
      { path: 'users', name: 'admin-users', component: () => import('../views/admin/AdminUsers.vue') },
      { path: 'users/:id', name: 'admin-user-detail', component: () => import('../views/admin/AdminUserDetail.vue'), meta: { kycAdmin: true } },
      { path: 'kyc', name: 'admin-kyc', component: () => import('../views/admin/AdminKyc.vue'), meta: { kycAdmin: true } },
      { path: 'catalog', name: 'admin-catalog', component: () => import('../views/admin/AdminCatalog.vue') },
      { path: 'payments', name: 'admin-payments', component: () => import('../views/admin/AdminPayments.vue') },
      { path: 'settings', name: 'admin-settings', component: () => import('../views/admin/AdminSettings.vue') },
      { path: 'system', name: 'admin-system', component: () => import('../views/admin/AdminSystem.vue') },
      { path: 'api-keys', name: 'admin-api-keys', component: () => import('../views/admin/AdminApiKeys.vue') },
      { path: 'audit-log', name: 'admin-audit-log', component: () => import('../views/admin/AdminAuditLog.vue') },
      { path: 'cms', name: 'admin-cms', component: () => import('../views/admin/AdminCms.vue') },
      // Intentionally not in the admin nav: a demo/QA tool, reached by URL only.
      { path: 'sandbox', name: 'admin-sandbox', component: () => import('../views/admin/AdminSandbox.vue') },
    ],
  },
  { path: '/403', name: 'forbidden', component: () => import('../views/errors/ForbiddenPage.vue') },
  { path: '/500', name: 'server-error', component: () => import('../views/errors/ServerErrorPage.vue') },
  { path: '/:pathMatch(.*)*', name: 'not-found', component: () => import('../views/errors/NotFoundPage.vue') },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior() {
    return { top: 0 };
  },
});

router.beforeEach(async (to) => {
  if (!to.meta.requiresAuth) return true;
  const auth = useAuthStore();
  if (!auth.initialized) await auth.fetchUser();
  if (!auth.isAuthenticated) {
    return { name: 'login', query: { next: to.fullPath } };
  }
  if (to.meta.requiresAdmin) {
    const role = auth.user?.role;
    // KYC_ADMIN only reaches routes flagged `kycAdmin` (KYC queue, user detail); the admin home sends them to the queue.
    if (role === 'KYC_ADMIN' && !to.meta.kycAdmin) {
      return to.name === 'admin-dashboard' ? { name: 'admin-kyc' } : { name: 'forbidden' };
    }
    if (role !== 'ADMIN' && role !== 'KYC_ADMIN') return { name: 'forbidden' };
  }
  return true;
});
